import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Injector,
  Input,
  OnDestroy,
  Output,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import {
  DefaultItemsToShow,
  MoreMenuItemId,
  itemWithInternalUrls,
} from './nav-menu.config';
import { NgStyle, NgTemplateOutlet } from '@angular/common';
import { Subject, fromEvent } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';

import { AnalyticsService } from '../../../../common/services/analytics-wrapper/analytics.service';
import { BreakpointState } from '@angular/cdk/layout';
import { CommonUtility } from '../../../../common/utils/commonUtils';
import { INavMenuItem } from '../../../../common/models/interfaces/IInclusion';
import { MatMenuModule } from '@angular/material/menu';
import { MenuitemsService } from '../../../../common/services/menu-items/menu-items.service';
import { ROUTER_CONSTANTS } from '../../../../common/constants/router.constants';
import { RenderLabelPipe } from '../../../../common/pipes/render-label/render-label.pipe';
import { Router } from '@angular/router';
import { SymphonyDrilldownFacade } from '../../../../common/facades/symphony-drilldown/symphony-drilldown.facade';
import { ViewportService } from '../../../../common/services/viewport/viewport.service';

@Component({
  selector: 'app-nav-menu',
  templateUrl: './nav-menu.component.html',
  styleUrls: ['./nav-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatMenuModule, NgTemplateOutlet, RenderLabelPipe, NgStyle],
})
export class NavMenuComponent implements AfterViewInit, OnDestroy {
  @Input() menuItems: INavMenuItem[];
  @Output() unitAPIError: EventEmitter<any> = new EventEmitter();
  @ViewChild('menuWrapperRef') menuWrapperRef: ElementRef;
  @ViewChildren('menuItemsRef') menuItemsRef: QueryList<ElementRef>;
  moreItems: INavMenuItem = {
    label: 'lbl_menuitem_more',
    id: MoreMenuItemId,
    components: [],
  };
  menuItemList: INavMenuItem[];
  ngUnsubscribe: Subject<void> = new Subject<void>();
  isDesktop: boolean = true;
  symphonyDrilldownFacade: SymphonyDrilldownFacade;
  selectedMenuItem: INavMenuItem;

  constructor(
    private readonly analyticsService: AnalyticsService,
    private readonly cdr: ChangeDetectorRef,
    private readonly menuItemService: MenuitemsService,
    private readonly router: Router,
    private readonly viewportService: ViewportService,
    private readonly injector: Injector,
  ) {
    this.symphonyDrilldownFacade = new SymphonyDrilldownFacade(this.injector);
    this.symphonyDrilldownFacade.ngUnsubscribe$ = this.ngUnsubscribe;
    this.subscribeToUnitApiError();

    this.setNavMenuStyle();

    this.viewportService.desktopObserver
      .pipe(
        takeUntil(this.ngUnsubscribe),
        debounceTime(200),
        distinctUntilChanged(),
      )
      .subscribe((result: BreakpointState) => {
        this.isDesktop = result.matches;
        this.cdr.detectChanges();
      });
  }

  /**
   * Subscribe to Unit API error for Symphony/Symphony Plus
   */
  subscribeToUnitApiError(): void {
    this.symphonyDrilldownFacade.unitApiErrorSubject$
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((isError: boolean) => {
        if (isError) {
          this.unitAPIError.emit();
        }
      });
  }

  /**
   * set CSS theme vars
   */
  setNavMenuStyle(): void {
    CommonUtility.updateCSSVariable('--menu-item-text-color', 'var(--grey-1)');
    CommonUtility.updateCSSVariable(
      '--menu-item-text-hover-color',
      'var(--primary-black)',
    );
    CommonUtility.updateCSSVariable(
      '--menu-popup-bg-color',
      'var(--primary-white)',
    );
  }

  /**
   * After view iniit lifecycle method
   */
  ngAfterViewInit(): void {
    this.doAdapt();
    fromEvent(window, 'resize')
      .pipe(debounceTime(100))
      .subscribe(() => {
        this.doAdapt();
      });
  }

  /**
   * method to make the menu items container responsive
   */
  doAdapt(): void {
    if (this.isDesktop) {
      // Adding dummy child component under more, so that more will show arrow beside the More word and will give true width
      this.moreItems.components = [
        {
          id: '',
          label: '',
          routerLink: '',
        },
      ];
      // Showing all items together with more
      this.menuItemList = [...this.menuItems, this.moreItems];
      this.cdr.detectChanges();

      // based on default limit to show, will move the rest under more.
      const mainEle: INavMenuItem[] = this.menuItems.slice(
        0,
        DefaultItemsToShow,
      );
      const restEle: INavMenuItem[] = this.menuItems.slice(DefaultItemsToShow);
      if (this.menuItems.length > DefaultItemsToShow) {
        this.moreItems.components.unshift(...restEle);
      }
      this.menuItemList = [...mainEle, this.moreItems];
      this.cdr.detectChanges();

      // based on size of container, will move the rest which wont fit under more
      const htmlElements: HTMLElement[] = this.menuItemsRef
        ?.toArray()
        .map((x) => x.nativeElement);
      const moreBtnWidth: number = htmlElements.slice(-1)[0]?.offsetWidth;
      const listContainerWidth: number =
        this.menuWrapperRef?.nativeElement?.offsetWidth;
      let stopWidth: number = moreBtnWidth;
      let stopCheck: boolean = true;
      const listItemsWithoutMore: HTMLElement[] = htmlElements.slice(0, -1);
      // starting with more button width, will now keep adding widths of indiviual elements until container width is not crossing
      listItemsWithoutMore.forEach((item: HTMLElement, i: number) => {
        if (
          (listContainerWidth >= stopWidth + item?.offsetWidth ||
            // this case is when we have only equal to or less than 4 elements in total and
            // need to decide if the last button should be more or not when menu item can fit.
            (i === listItemsWithoutMore.length - 1 &&
              this.moreItems?.components?.length === 1 &&
              listContainerWidth >=
                stopWidth + item?.offsetWidth - moreBtnWidth)) &&
          stopCheck
        ) {
          stopWidth += item?.offsetWidth;
        } else {
          // once we reach container width, we move the rest under more.
          stopCheck = false;
          this.moreItems?.components?.unshift(this.menuItemList[i]);
          mainEle.splice(
            mainEle.findIndex(
              (elem: INavMenuItem) => elem.id === this.menuItemList[i].id,
            ),
            1,
          );
        }
      });
      // removing the dummy element under more
      this.moreItems.components.pop();
      // will add more to the container only if it has something under it
      this.menuItemList = [
        ...mainEle,
        ...(this.moreItems?.components?.length ? [this.moreItems] : []),
      ];
      this.cdr.detectChanges();
    }
  }

  /**
   * On Header Nav menu link clicked
   * @param menuItem menu Item object
   */
  navigateOnItemClick(menuItem: INavMenuItem): void {
    switch (menuItem?.id) {
      case 'dcCreateCase':
        this.router.navigate([`${ROUTER_CONSTANTS.createCase}`]);
        break;
      default:
        this.menuItemService
          .onMenuItemClick(menuItem, this.symphonyDrilldownFacade)
          .pipe(takeUntil(this.ngUnsubscribe))

          .subscribe();
    }
  }

  /**
   * Handle navigation or set selected item in case of nesting
   * @param event Click Event on button
   * @param selectedItem Clicked item
   * @param parentItem parent item in nested hierarchy
   */
  handleMenuItemClick(
    event: MouseEvent,
    selectedItem: INavMenuItem,
    parentItem?: INavMenuItem,
  ): void {
    if (selectedItem.components?.length) {
      // when there are child items, stop event bubbling else it will close the menu flyout
      event.stopPropagation();
      this.selectedMenuItem = selectedItem;
    } else {
      this.navigateOnItemClick(selectedItem);
      this.tagOnNavClick(selectedItem, parentItem?.analyticLabel);
    }
  }

  /**
   * Handle analysitcs on itme click
   * @param item Selected item
   * @param parentServiceName Parent analytics title in case sof nesting
   */
  tagOnNavClick(item: INavMenuItem, parentServiceName: string = ''): void {
    let url: string | undefined = item.routerLink;

    if (!itemWithInternalUrls.has(item.id)) {
      url = '#';
    }
    if (url) {
      const linkName: string = [parentServiceName, item.analyticLabel]
        .filter((value: string | undefined) => !!value)
        .join(' - ');

      this.analyticsService.createLinkClickTagObject(
        url,
        linkName,
        'Main Navigation Menu Clicked',
        itemWithInternalUrls.has(item.id) ? 'Internal' : 'External',
        { link_section: 'Main Navigation Menu' },
      );
    }
  }

  /**
   * Returns number of items to show per column
   * For <=12 -> 3 items; <=16 -> 4 items; <=20 -> 5 items and so on
   * @param totalCount Toal menu items
   * @returns item count in column
   */
  getColumnItemCount(totalCount: number): number {
    return Math.max(3, Math.ceil(totalCount / 4));
  }

  /**
   * Angular lifecycle hook
   */
  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
}
